import { createSelector } from 'reselect';
import { RootState } from '../../../../types';
import { EntityTypes } from '../../../../constants/entities';
import { IEntityReducer } from '../../../reducers/entities';
import { IScene, ISceneConfig, ISceneConfigDAO } from 'types/domain/scene';
import { IPosition, IRotation } from 'types/domain/base';
import { IAction } from 'types/domain/action';
import { IImage } from 'types/domain/image';
import { IActionFeed, IActionFeedItem, selectActionFeed } from '../action/actionFeed';
import { PortraitState } from 'core/slices/player';
import { createCachedSelector } from 're-reselect';

const entity = (state: RootState) => state.entity.present;
const sceneId = (state: RootState, sId, aId) => sId ?? state.state[EntityTypes.SCENE];
const actionId = (state: RootState, sId, aId) => aId ?? state.state[EntityTypes.ACTION];
const portrait = (state: RootState) => state.core.player.state.portrait;
const feed = selectActionFeed;

export const selectSceneConfig = createCachedSelector(
  entity,
  sceneId,
  actionId,
  feed,
  portrait,
  (entity: IEntityReducer, sceneId: number, actionId: number, feed: IActionFeed, portrait: PortraitState) => {
    const action: IAction = entity[EntityTypes.ACTION][actionId];
    const scene: IScene = action?.sceneId
      ? entity[EntityTypes.SCENE][action.sceneId]
      : entity[EntityTypes.SCENE][sceneId];

    if (!scene) return null;

    const currentAction: IActionFeedItem = feed.feed[feed.index];
    const nextItem: IActionFeedItem = feed.feed[feed.index + 1];
    const prevItem: IActionFeedItem = feed.feed[feed.index - 1];
    const nextAction: IAction = entity[EntityTypes.ACTION][nextItem?.actionId];

    const actionConfig: ISceneConfig = action
      ? action.sceneId === sceneId
        ? entity[EntityTypes.SCENE_CONFIG][action.sceneConfigId]
        : currentAction &&
          currentAction.actionId &&
          entity[EntityTypes.ACTION][currentAction.actionId] &&
          entity[EntityTypes.ACTION][currentAction.actionId].sceneId === sceneId
        ? entity[EntityTypes.SCENE_CONFIG][entity[EntityTypes.ACTION][currentAction.actionId]?.sceneConfigId]
        : prevItem && prevItem.actionId && entity[EntityTypes.ACTION][prevItem.actionId]?.sceneId === sceneId
        ? entity[EntityTypes.SCENE_CONFIG][entity[EntityTypes.ACTION][prevItem.actionId]?.sceneConfigId]
        : nextItem && nextItem.actionId && entity[EntityTypes.ACTION][nextItem.actionId]?.sceneId === sceneId
        ? entity[EntityTypes.SCENE_CONFIG][entity[EntityTypes.ACTION][nextItem.actionId]?.sceneConfigId]
        : null
      : null;

    return parseSceneConfigDAO(
      entity,
      mergeConfigs(entity[EntityTypes.SCENE_CONFIG][scene.sceneConfigId], actionConfig),
      nextAction ? entity[EntityTypes.SCENE_CONFIG][nextAction.sceneConfigId] : null,
      portrait,
    ) as ISceneConfigDAO;
  },
)((state: RootState, sId?, aId?) => aId || sId || state.state[EntityTypes.ACTION] || 'unknown');

export const selectVoiceSceneConfig = createCachedSelector(
  entity,
  sceneId,
  actionId,
  feed,
  portrait,
  (entity: IEntityReducer, sceneId: number, actionId: number, feed: IActionFeed, portrait: PortraitState) => {
    const action: IAction = entity[EntityTypes.ACTION][actionId];
    const scene: IScene =
      action && action.voiceSceneId
        ? entity[EntityTypes.SCENE][action.voiceSceneId]
        : entity[EntityTypes.SCENE][sceneId];

    if (!scene) return null;

    const currentAction: IActionFeedItem = feed.feed[feed.index];
    const nextItem: IActionFeedItem = feed.feed[feed.index + 1];
    const prevItem: IActionFeedItem = feed.feed[feed.index - 1];
    const nextAction: IAction = entity[EntityTypes.ACTION][nextItem?.actionId];

    const actionConfig: ISceneConfig = !!action
      ? !!sceneId && action.voiceSceneId === sceneId
        ? entity[EntityTypes.SCENE_CONFIG][action.voiceSceneConfigId]
        : currentAction &&
          currentAction.actionId &&
          entity[EntityTypes.ACTION][currentAction.actionId]?.voiceSceneId === sceneId
        ? entity[EntityTypes.SCENE_CONFIG][entity[EntityTypes.ACTION][currentAction.actionId]?.voiceSceneConfigId]
        : prevItem &&
          !!sceneId &&
          prevItem.actionId &&
          entity[EntityTypes.ACTION][prevItem.actionId]?.voiceSceneId === sceneId
        ? entity[EntityTypes.SCENE_CONFIG][entity[EntityTypes.ACTION][prevItem.actionId]?.voiceSceneConfigId]
        : nextItem &&
          nextItem.actionId &&
          !!sceneId &&
          entity[EntityTypes.ACTION][nextItem.actionId]?.voiceSceneId === sceneId
        ? entity[EntityTypes.SCENE_CONFIG][entity[EntityTypes.ACTION][nextItem.actionId]?.voiceSceneConfigId]
        : null
      : null;

    return parseSceneConfigDAO(
      entity,
      mergeConfigs(entity[EntityTypes.SCENE_CONFIG][scene.sceneConfigId], actionConfig),
      nextAction ? entity[EntityTypes.SCENE_CONFIG][nextAction.voiceSceneConfigId] : null,
      portrait,
      false,
    ) as ISceneConfigDAO;
  },
)((state: RootState, sId?, aId?) => aId || sId || state.state[EntityTypes.ACTION] || 'unknown');

function mergeConfigs(sceneConfig, actionConfig) {
  if (!sceneConfig) return null;

  if (!actionConfig) return sceneConfig;

  return Object.keys(actionConfig).reduce((prev, key) => {
    return { ...prev, [key]: actionConfig[key] !== null ? actionConfig[key] : sceneConfig[key] };
  }, {}) as ISceneConfig;
}

function parseSceneConfigDAO(
  entities: IEntityReducer,
  sceneConfig: ISceneConfig,
  nextConfig: ISceneConfig,
  portrait,
  defaultPortrait = true,
): Partial<ISceneConfigDAO> {
  if (!sceneConfig) return {};

  const pId = portrait
    ? sceneConfig.pPositionId || (defaultPortrait && sceneConfig.positionId)
    : sceneConfig.positionId;
  const rId = portrait
    ? sceneConfig.pRotationId || (defaultPortrait && sceneConfig.rotationId)
    : sceneConfig.rotationId;
  const nPId = nextConfig ? (portrait ? nextConfig.pPositionId || nextConfig.positionId : nextConfig.positionId) : null;
  const nRId = nextConfig ? (portrait ? nextConfig.pRotationId || nextConfig.rotationId : nextConfig.rotationId) : null;
  const iId = portrait ? sceneConfig.pImageId || sceneConfig.imageId : sceneConfig.imageId;
  const fit = portrait ? sceneConfig.pFit || sceneConfig.fit : sceneConfig.fit;
  const nextfit = nextConfig ? (portrait ? nextConfig.pFit || nextConfig.fit : nextConfig.fit) : null;
  const position: IPosition = pId ? entities[EntityTypes.POSITION][pId] : null;
  const rotation: IRotation = rId ? entities[EntityTypes.ROTATION][rId] : null;
  const nextPosition: IPosition = nPId ? entities[EntityTypes.POSITION][nPId] : null;
  const nextRotation: IRotation = nRId ? entities[EntityTypes.ROTATION][nRId] : null;
  const image: IImage = iId ? entities[EntityTypes.IMAGE][iId] : null;
  const shouldAnimate =
    fit !== nextfit ||
    nextPosition?.x !== position?.x ||
    nextPosition?.y !== position?.y ||
    nextPosition?.x !== position?.x;

  return {
    ...sceneConfig,
    position,
    rotation,
    nextPosition,
    nextRotation,
    image,
    fit,
    shouldAnimate,
  };
}
